import { UploadType } from '@/Types/Enums/uploadType';
import BaseUploadModel from '@/Models/baseUploadModel';
import Upload from '@/Types/upload';
import { getRootFolderNameFromRelativePath } from '@/VueComponents/Uploads/utils/uploadsUtils';
import { UploadStatus } from '@/Types/Enums/uploadStatus';

export default class UploadModel implements BaseUploadModel {
  uploadId: string;
  requestId: string;
  uploadToken: string;
  addedTimestamp: string;
  fileName: string;
  size: number;
  fileRelativePath: string;
  personaDisplayName: string;
  children: BaseUploadModel[] = [];

  rootFolderName: string | null;
  comments: string;
  nativeBrowserFile: File;

  type: UploadType;

  cameraName: string;
  statusName: UploadStatus;
  progress: number;
  bytesUploaded: number;

  get isAuthorising(): boolean {
    return this.statusName === UploadStatus.Authorising;
  }

  hasUploadInfoChanged: boolean = false;
  isFolder: boolean = false;

  static fromFile(file: File, type: UploadType, fileRelativePath?: string): UploadModel {
    const model = new UploadModel();

    model.addedTimestamp = new Date().toUTCString();
    model.bytesUploaded = 0;
    model.progress = 0;
    model.fileName = file.name;
    model.size = file.size;
    model.fileRelativePath = fileRelativePath ? fileRelativePath : file.webkitRelativePath;
    model.rootFolderName = model.fileRelativePath ?
      getRootFolderNameFromRelativePath(model.fileRelativePath) : null;
    model.statusName = UploadStatus.Authorising;
    model.nativeBrowserFile = file;
    model.type = type;

    return model;
  }

  static fromServerUpload(upload: Upload): UploadModel {
    const model = new UploadModel();

    model.uploadId = upload.uploadId;
    model.requestId = upload.requestId ?? upload.selfResponseId!;
    model.uploadToken = upload.uploadToken;
    model.addedTimestamp = upload.addedTimestamp;
    model.fileName = upload.fileName;
    model.size = upload.fileSize;
    model.fileRelativePath = upload.fileRelativePath ? upload.fileRelativePath : upload.fileName;
    model.personaDisplayName = upload.personaDisplayName;

    model.rootFolderName = getRootFolderNameFromRelativePath(upload.fileRelativePath);
    model.comments = upload.comments;

    model.type = upload.type;

    model.cameraName = upload.cameraName;
    model.statusName = upload.uploadStatusName as UploadStatus;
    model.progress = model.statusName === UploadStatus.Uploaded ? 100 : 0;
    model.bytesUploaded = upload.bytesUploaded;

    return model;
  }

  getSortingValue(fieldName: string) {
    switch (fieldName) {
      case 'addedTimestamp':
        return this.addedTimestamp;
      case 'fileName':
        return this.fileName;
      case 'size':
        return this.size;
      case 'statusName':
        return this.statusName;
      case 'userName':
        return this.personaDisplayName;
      case 'path':
        return this.fileRelativePath;
    }
  }

  getAllIds() {
    return [this.uploadId];
  }

  getAllStatuses(): string[] {
    return [this.statusName];
  }

  getUploadsLength() {
    // Single upload model has only one upload
    return 1;
  }

  updateWithServerData(serverUpload: Upload) {
    this.uploadId = serverUpload.uploadId;
    this.requestId = serverUpload.requestId ?? serverUpload.selfResponseId!;
    this.uploadToken = serverUpload.uploadToken;
    this.addedTimestamp = serverUpload.addedTimestamp;
    this.statusName = serverUpload.uploadStatusName as UploadStatus;
  }

  setFailedAuthorization() {
    this.statusName = UploadStatus.FailedAuthorisation;
  }
}